import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import {
  ActionsBanner,
  ColumnsButton,
  ColumnsDrawer,
  CreateButton,
  DeleteConfirmation,
  ExportDialog,
  ExportDropdown,
  FiltersBar,
  Page,
  PeriodFilter,
} from '~/components';
import { BulkActionMenu } from '~/components/bulk-action';
import {
  AllocationFiltersBar,
  AllocationFiltersGroup,
  ClientFiltersBar,
  ClientFiltersGroup,
  FilterButton,
  FiltersDrawer,
  MemberFiltersBar,
  MemberFiltersGroup,
  ProjectFiltersBar,
  ProjectFiltersGroup,
  ResourceFiltersBar,
  ResourceFiltersGroup,
} from '~/components/filters';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import {
  useAllocationFilters,
  useAuth,
  useClientFilters,
  useDocumentTitle,
  useIsMounted,
  useMemberFilters,
  useProjectFilters,
  useResourceFilters,
  useRowSelection,
  useSearchParams,
  useSearchParamsConfig,
  useViewColumns,
} from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { QuerySort, dateFormats, intervalOptions, mimeTypes } from '~/utils';
import AllocationDetails from '../dialogs/AllocationDetails';
import AllocationForm from '../dialogs/AllocationForm';
import AllocationsList from './AllocationsList';
import { BulkDelete, BulkReassign, BulkSetBookingType, BulkShift } from './bulk';

export default function AllocationsListPage() {
  const documentTitle = useDocumentTitle('Resource Allocations');

  const { workspace } = useWorkspace();
  const api = useApi();
  const isMounted = useIsMounted();
  const confirmation = useConfirmation();
  const history = useHistory();
  const location = useLocation();
  const { url, path } = useRouteMatch();
  const auth = useAuth();
  const canManage = auth.allocations.manage;

  const searchParamsConfig = useSearchParamsConfig();
  const allocationFilters = useAllocationFilters();
  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters();
  const memberFilters = useMemberFilters();
  const resourceFilters = useResourceFilters();

  const periodIntervals = useMemo(
    () => [
      intervalOptions.all_dates,
      intervalOptions.next_3_months,
      intervalOptions.next_6_months,
      intervalOptions.next_9_months,
      intervalOptions.next_12_months,
      intervalOptions.custom,
    ],
    [],
  );

  const allocationFormInitialValues = useMemo(
    () => ({ start: moment().format(dateFormats.isoDate), end: moment().format(dateFormats.isoDate) }),
    [],
  );

  const [query, setQuery] = useState({
    isReady: false,
    searchParamsStatus: 'pending',
    data: null,
    action: 'load',
    params: {
      sort: new QuerySort('start', 'asc'),
      page: 0,
      size: 50,
      period: intervalOptions.next_12_months,
      ...allocationFilters.filters,
      ...clientFilters.filters,
      ...projectFilters.filters,
      ...memberFilters.filters,
      ...resourceFilters.filters,
    },
  });

  const { isReady, data, params, searchParamsStatus, action } = query;

  const selection = useRowSelection({ rows: data?.results });

  const setParams = (values) => {
    setQuery((state) => ({
      ...state,
      action: 'filter',
      params: { ...state.params, ...values, page: 0 },
      searchParamsStatus: 'ready',
    }));
  };

  const loadMore = useCallback(() => {
    setQuery((state) => {
      if (
        state.searchParamsStatus !== 'ready' ||
        state.action !== null ||
        !state.data ||
        state.data.total <= state.data.results.length
      ) {
        return state;
      }

      return { ...state, params: { ...state.params, page: state.params.page + 1 }, action: 'load-more' };
    });
  }, []);

  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        sort: { default: new QuerySort('start', 'asc'), ...searchParamsConfig.sort },
        period: {
          ...searchParamsConfig.period,
          default: intervalOptions.next_12_months,
          intervals: periodIntervals,
        },
        ...allocationFilters.searchParamsConfig,
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
        ...memberFilters.searchParamsConfig,
        ...resourceFilters.searchParamsConfig,
      }),
      [
        searchParamsConfig,
        allocationFilters,
        clientFilters,
        projectFilters,
        memberFilters,
        resourceFilters,
        periodIntervals,
      ],
    ),
    sessionKey: 'allocations_list',
    onChange: useCallback((values) => {
      if (values) setParams(values);
    }, []),
  });

  useEffect(() => {
    if (searchParamsStatus !== 'pending') return;
    searchParams.get().then((values) => {
      if (values) setParams(values);
    });
  }, [searchParams, searchParamsStatus]);

  const urlSearchParams = useMemo(
    () => ({
      sort: params.sort,
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      ...allocationFilters.mapUrlSearchParams(params),
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
      ...memberFilters.mapUrlSearchParams(params),
      ...resourceFilters.mapUrlSearchParams(params),
    }),
    [params, allocationFilters, clientFilters, projectFilters, memberFilters, resourceFilters],
  );

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .allocations()
        .list({ ...urlSearchParams, page: params.page, size: params.size });

      if (!isMounted.current) return;

      setQuery((state) => ({
        ...state,
        action: null,
        isReady: true,
        searchParamsStatus: 'ready',
        data: {
          ...data,
          results: state.action === 'load-more' ? [...state.data.results, ...data.results] : data.results,
          total: data.total,
        },
      }));
    } catch {
      setQuery((state) => ({ ...state, action: null, isReady: true, data: { total: 0, results: [] } }));
    }
  }, [workspace.id, urlSearchParams, api, params.page, params.size, isMounted]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);

  const handleFilter = (values) => {
    selection.clear();
    setParams(values);
    searchParams.set(_.omit(values, 'sort'));
    hideFilters();
  };

  const handleSort = ({ column, sort }) => {
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ sort: querySort });
    searchParams.set({ sort: querySort });
    selection.clear();
  };

  const handleBulkComplete = () => {
    selection.clear();
    setParams({});
  };

  const openDrawer = (mode, allocation) =>
    history.push({ pathname: `${url}/${mode}/${allocation.id}`, search: location.search });

  const handleCreate = () => history.push({ pathname: `${url}/new`, search: location.search });

  const handleRowClick = (allocation) => openDrawer(canManage ? 'edit' : 'view', allocation);

  const handleView = (allocation) => openDrawer('view', allocation);

  const handleEdit = (allocation) => openDrawer('edit', allocation);

  const handleDelete = async (allocation) => {
    const confirm = await confirmation.prompt((resolve) => (
      <DeleteConfirmation resolve={resolve}>Are you sure you want to delete this allocation?</DeleteConfirmation>
    ));
    if (!confirm) return;

    await api.www.workspaces(workspace.id).allocations(allocation.id).delete();
    setParams({});
  };

  const handleDrawerClose = () => {
    history.push({ pathname: url, search: location.search });
    documentTitle.set('Resource Allocations');
  };

  const handleAllocationChanged = () => setParams({});

  const handleBulkDelete = () =>
    confirmation.prompt((resolve) => (
      <BulkDelete ids={selection.ids} onComplete={handleBulkComplete} onClose={resolve} />
    ));

  const handleBulkSetBookingType = () =>
    confirmation.prompt((resolve) => (
      <BulkSetBookingType ids={selection.ids} onComplete={handleBulkComplete} onClose={resolve} />
    ));

  const handleBulkReassign = () =>
    confirmation.prompt((resolve) => (
      <BulkReassign ids={selection.ids} onComplete={handleBulkComplete} onClose={resolve} />
    ));

  const handleBulkShift = () =>
    confirmation.prompt((resolve) => (
      <BulkShift ids={selection.ids} onComplete={handleBulkComplete} onClose={resolve} />
    ));

  const handleExport = (filename, mimeType) =>
    confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onLoad={api.www
          .workspaces(workspace.id)
          .allocations()
          .export({ ...urlSearchParams, size: null }, { headers: { accept: mimeType }, responseType: 'blob' })}
        onClose={resolve}
      />
    ));

  const [dialog, setDialog] = useState(null);

  const columns = useViewColumns({
    storageKey: 'allocations_list_columns',
    columns: [
      { key: 'resource', name: 'Resource', disabled: true },
      { key: 'project', name: 'Assignment', disabled: true },
      { key: 'role', name: 'Role', default: true },
      { key: 'task', name: 'Task', default: false },
      { key: 'bookingType', name: 'Booking Type', default: true },
      { key: 'billable', name: 'Billable Type', default: true },
      { key: 'start', name: 'Start', default: true },
      { key: 'end', name: 'End', default: true },
      { key: 'hours', name: 'Hours', default: true },
      { key: 'totalHours', name: 'Total Allocated', default: false },
    ],
  });

  const handleShowColumnsDrawer = () => {
    setDialog(
      <ColumnsDrawer
        value={columns.selected}
        columns={columns.all}
        onClose={() => setDialog(null)}
        onApply={(value) => {
          columns.save(value);
          setDialog(null);
        }}
      />,
    );
  };

  if (!isReady || !data) return <PageLoader />;

  return (
    <Page>
      <Page.Header>
        <Page.Info>
          <Page.Eyebrow>Resources</Page.Eyebrow>
          <Page.Title>Resource Allocations</Page.Title>
        </Page.Info>

        <Page.Actions>
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport('allocations.csv', mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport('allocations.xlsx', mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <ColumnsButton onClick={handleShowColumnsDrawer} />
          <FilterButton isOutline onClick={showFilters} />

          <CreateButton
            tooltip={canManage ? 'New Allocation' : 'Your security role prohibits you from creating allocations.'}
            onClick={handleCreate}
            disabled={!canManage}
          />
        </Page.Actions>
      </Page.Header>

      <Page.Filters>
        <FiltersBar>
          <PeriodFilter
            name="period"
            clearable={false}
            placeholder="Date Range"
            value={params.period}
            intervals={periodIntervals}
            onChange={({ value }) => handleFilter({ period: value })}
          />

          <AllocationFiltersBar filters={params} sticky={['allocationBookingTypeId']} onChange={handleFilter} />

          <ClientFiltersBar filters={params} onChange={handleFilter} />

          <ProjectFiltersBar filters={params} onChange={handleFilter} />

          <MemberFiltersBar filters={params} onChange={handleFilter} />

          <ResourceFiltersBar filters={params} sticky={['resourceTypeId']} onChange={handleFilter} />
        </FiltersBar>
      </Page.Filters>

      <AllocationsList
        data={data}
        action={action}
        query={params}
        columns={columns}
        canManage={canManage}
        selection={selection}
        onLoadMore={loadMore}
        onRowClick={handleRowClick}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onSort={handleSort}
      />

      {canManage && selection.any && (
        <ActionsBanner>
          <BulkActionMenu count={selection.count}>
            {({ setIsOpen }) => (
              <>
                <BulkActionMenu.Item
                  onClick={() => {
                    handleBulkSetBookingType();
                    setIsOpen(false);
                  }}>
                  Set Booking Type
                </BulkActionMenu.Item>

                <BulkActionMenu.Item
                  onClick={() => {
                    handleBulkReassign();
                    setIsOpen(false);
                  }}>
                  Reassign Resource
                </BulkActionMenu.Item>

                <BulkActionMenu.Item
                  onClick={() => {
                    handleBulkShift();
                    setIsOpen(false);
                  }}>
                  Shift Dates
                </BulkActionMenu.Item>

                <BulkActionMenu.Item
                  onClick={() => {
                    handleBulkDelete();
                    setIsOpen(false);
                  }}>
                  Delete
                </BulkActionMenu.Item>
              </>
            )}
          </BulkActionMenu>
        </ActionsBanner>
      )}

      <Filters
        values={params}
        periodIntervals={periodIntervals}
        isOpen={filtersVisible}
        onApply={handleFilter}
        onClose={hideFilters}
      />

      <Switch>
        {auth.allocations.manage && (
          <Route path={[`${path}/new`, `${path}/edit/:allocationId`]}>
            <AllocationForm
              initialValues={allocationFormInitialValues}
              onSaved={handleAllocationChanged}
              onDeleted={handleAllocationChanged}
              onClose={handleDrawerClose}
            />
          </Route>
        )}

        <Route path={`${path}/view/:allocationId`}>
          <AllocationDetails onClose={handleDrawerClose} />
        </Route>
      </Switch>

      {dialog}
    </Page>
  );
}

function Filters({ values, periodIntervals, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleFilter = (filter) => setFilters({ ...filters, ...filter });

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <FiltersDrawer isOpen={isOpen} onApply={() => onApply(filters)} onClose={handleCancel}>
      <PeriodFilter
        name="period"
        clearable={false}
        placeholder="Date Range"
        value={filters.period}
        intervals={periodIntervals}
        onChange={({ value }) => handleFilter({ period: value })}
      />

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />

      <ResourceFiltersGroup filters={filters} onChange={handleFilter} />

      <AllocationFiltersGroup filters={filters} onChange={handleFilter} />
    </FiltersDrawer>
  );
}
